import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Inbox, PlayCircle, CheckCircle2, Plus, ArrowRight, Loader2, Clock, AlertTriangle,
} from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../lib/AppContext";
import TaskForm from "../components/TaskForm";
import CicloAccordion from "../components/CicloAccordion";
import { formatDate } from "../lib/format";

// Bug 4/13 · Eventi puntuali NON sono cicli. Restano confinati in Agenda.
const EVENT_TYPES = new Set(["event", "call", "meeting", "appointment"]);

// ─── Card component ───────────────────────────────────────
function CicliCard({ to, icon: Icon, iconColor, title, subtitle, count, loading, testid }) {
  return (
    <Link
      to={to}
      className="pxos-card group p-6 sm:p-7 flex flex-col justify-between min-h-[180px] transition hover:shadow-md hover:-translate-y-0.5"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-4">
        <div
          className="rounded-2xl p-3 shrink-0"
          style={{ background: iconColor + "1a", color: iconColor }}
        >
          <Icon size={28} />
        </div>
        <ArrowRight size={18} className="opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition mt-2" />
      </div>
      <div className="mt-4">
        <div className="text-4xl font-display font-semibold tracking-tight" data-testid={`${testid}-count`}>
          {loading ? <Loader2 size={26} className="animate-spin opacity-60" /> : count}
        </div>
        <div className="mt-1 text-lg font-medium" data-testid={`${testid}-title`}>{title}</div>
        <div className="text-xs opacity-70 mt-0.5">{subtitle}</div>
      </div>
    </Link>
  );
}

// ─── Preview: prossimi 3 cicli ─────────────────────────────
function ProssimiPreview({ items, areas }) {
  const nav = useNavigate();
  if (!items?.length) return null;
  const byArea = new Map(areas.map(a => [a.id, a]));
  return (
    <div className="pxos-card p-5 sm:p-6" data-testid="cicli-prossimi">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-semibold">Prossimi cicli in agenda</h2>
        <button
          onClick={() => nav("/agenda?view=week")}
          className="text-xs opacity-70 hover:opacity-100 underline inline-flex items-center gap-1"
          data-testid="cicli-prossimi-view-agenda"
        >
          Apri agenda <ArrowRight size={12} />
        </button>
      </div>
      <ul className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
        {items.map((t) => {
          const primary = t.scheduled_date || t.due_date;
          const primaryLabel = t.scheduled_date ? "Programmato" : "Scadenza";
          const time = t.scheduled_time ? t.scheduled_time.slice(0, 5) : null;
          const area = t.area_id ? byArea.get(t.area_id) : null;
          return (
            <li key={t.id} className="py-2 flex items-center gap-3" data-testid={`cicli-prossimo-${t.id}`}>
              <Clock size={14} className="opacity-40 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{t.title}</div>
                <div className="text-xs opacity-70">
                  {primaryLabel}: {primary ? formatDate(primary) : "—"}
                  {time ? ` alle ${time}` : ""}
                  {area ? " · " : ""}
                  {area && (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px]"
                      style={{ background: (area.color || "#95BAEF") + "22", color: area.color || "#3d5a80" }}
                    >
                      {area.name}
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────
export default function Cicli() {
  const { user } = useApp();
  const [summary, setSummary] = useState(null);
  const [areas, setAreas] = useState([]);
  const [openCycles, setOpenCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sum, ar, allTasks] = await Promise.all([
        api.cicliSummary(),
        api.listAreas().catch(() => []),
        api.listTasks({ include_completed: false }).catch(() => []),
      ]);
      setSummary(sum);
      setAreas(ar || []);
      // Bug 18/50 · Cicli aperti = task top-level (NO azioni figlie), non eventi,
      // status ∈ open/in_progress/waiting, con almeno una data.
      const cycles = (allTasks || []).filter((t) =>
        !t.parent_task_id
        && !EVENT_TYPES.has(t.type)
        && !["completed", "cancelled"].includes(t.status)
        && (t.scheduled_date || t.due_date)
      );
      setOpenCycles(cycles);
    } catch (e) {
      setSummary({ counts: { da_aprire: 0, aperti: 0, chiusi: 0, chiusi_ultimi_7g: 0 }, prossimi: [] });
      setOpenCycles([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = summary?.counts || {};
  const firstName = user?.first_name || "";
  const areaById = useMemo(() => Object.fromEntries((areas || []).map((a) => [a.id, a])), [areas]);

  return (
    <div className="space-y-6" data-testid="cicli-landing">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest opacity-70">Operatività</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-1" data-testid="cicli-heading">
            I tuoi cicli aperti (percorsi con obiettivo)
          </h1>
          <p className="text-sm opacity-75 mt-1 max-w-xl">
            {firstName ? `Ciao ${firstName}, ` : ""}scegli dove vuoi lavorare oggi.
            I cicli senza data restano <em>Da aprire</em> finché non li metti in agenda.
          </p>
        </div>
        <button
          onClick={() => setFormOpen(true)}
          className="pxos-btn-primary inline-flex items-center gap-2"
          data-testid="cicli-add-btn"
        >
          <Plus size={16} /> Aggiungi ciclo
        </button>
      </div>

      {/* 3 Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <CicliCard
          to="/cicli/da-aprire"
          icon={Inbox}
          iconColor="#95BAEF"
          title="Da aprire"
          subtitle="Cicli senza data ancora da programmare"
          count={counts.da_aprire ?? 0}
          loading={loading}
          testid="cicli-card-da-aprire"
        />
        <CicliCard
          to="/cicli/aperti"
          icon={PlayCircle}
          iconColor="#f28c28"
          title="Aperti"
          subtitle="In corso, con data programmata o scadenza"
          count={counts.aperti ?? 0}
          loading={loading}
          testid="cicli-card-aperti"
        />
        <CicliCard
          to="/cicli/chiusi"
          icon={CheckCircle2}
          iconColor="#4a8a4f"
          title="Chiusi"
          subtitle={counts.chiusi_ultimi_7g
            ? `${counts.chiusi_ultimi_7g} negli ultimi 7 giorni`
            : "Storico cicli completati o annullati"}
          count={counts.chiusi ?? 0}
          loading={loading}
          testid="cicli-card-chiusi"
        />
      </div>

      {/* Preview prossimi 3 */}
      {!loading && <ProssimiPreview items={summary?.prossimi || []} areas={areas} />}

      {/* Bug 18/50 · Accordion cicli aperti — obiettivo, %, prossima azione, area, scadenza */}
      {!loading && openCycles.length > 0 && (
        <div className="space-y-3" data-testid="cicli-accordion-section">
          <h2 className="font-display text-lg font-semibold">Cicli aperti · dettaglio</h2>
          <p className="text-xs opacity-70 -mt-2">
            Espandi un ciclo per vedere le azioni e aggiungerne di nuove.
          </p>
          <div className="space-y-2">
            {openCycles.map((c) => (
              <CicloAccordion
                key={c.id}
                cycle={c}
                area={c.area_id ? areaById[c.area_id] : null}
                onChanged={load}
              />
            ))}
          </div>
        </div>
      )}

      {/* Hint quando non c'è nulla */}
      {!loading && counts.da_aprire === 0 && counts.aperti === 0 && counts.chiusi === 0 && (
        <div className="pxos-card p-6 text-center border-dashed" data-testid="cicli-empty-hint"
             style={{ borderStyle: "dashed" }}>
          <AlertTriangle size={22} className="mx-auto opacity-50" />
          <p className="mt-2 text-sm opacity-75">
            Non hai ancora cicli. Comincia dal pulsante <strong>+ Aggiungi ciclo</strong> qui sopra
            o parla con la <Link to="/segretaria" className="underline">Segretaria IA</Link>.
          </p>
        </div>
      )}

      {/* Modal creazione */}
      <TaskForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => { setFormOpen(false); load(); }}
      />
    </div>
  );
}
